/**
 * @file operations.ts
 * @module types/request_data/operations
 * @description Defines TypeScript request/response contracts for Operations module API operations (e.g. Branch Live Status), ensuring frontend submissions and consumption match backend expectations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.4.1
 */

export interface IBranchHealthParams {
  branchId?: string;
}

/**
 * Mirrors the server-side `BranchHealthStatuses` enum.
 * 1 Healthy, 2 Warning, 3 Critical, 4 NoActivity.
 */
export enum BranchHealthStatuses {
  Healthy = 1,
  Warning = 2,
  Critical = 3,
  NoActivity = 4,
}

/**
 * Server-controlled health tier. Drives card badge color; do not recompute
 * from `score` on the frontend — `level` is the single source of truth.
 */
export type BranchHealthLevel = "GOOD" | "WARNING" | "SERIOUS" | "CRITICAL";

export interface IBranchHealthRow {
  branchId: string;
  branchName: string;
  status: BranchHealthStatuses;
  activeRoutes: number;
  pendingOrders: number;
  overdueOrders: number;
  warnings: string[];
  unprocessedOrders: number;
  weightToShip: number;
  availableTonnage: number;
  enrouteVehicles: number;
  ordersOut: number;
  activeVehicles: number;
  totalVehicles: number;
  /** Always 0 — no data source exists yet. */
  pendingClearanceFinance: number;
  /** Always 0 — no data source exists yet. */
  pendingClearanceWarehouse: number;
  /** Always 0 — no data source exists yet. */
  brokenDownVehicles: number;
  /** Integer 0-100 health score, already rounded/clamped by the server. */
  score: number;
  /** Health tier — use this (not `score`) to color the card badge. */
  level: BranchHealthLevel;
  /** Ordered, human-readable issue descriptions; render verbatim, don't re-sort. */
  issues: string[];
}

export interface IBranchHealthResponse {
  records: IBranchHealthRow[];
  asOf: string;
}
